import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { TabPanel } from "@mui/lab";
import { Collapse, Grid, IconButton, Link, Tooltip, Typography } from "@mui/material";
import ArticleCard from "components/molecules/cards/ArticleCard";
import { useCatalogue } from "providers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import categories from "constants/Categories";
import AllMetiersCode from "constants/AllMetiersCode";

interface CategorieCataloguePageProps {
  categorieDatabaseLabel: string;
}

function CategorieCataloguePanel({
  categorieDatabaseLabel,
}: Readonly<CategorieCataloguePageProps>): ReactNode {
  const { articles, selectedMetierId } = useCatalogue();
  const [isAutresMetiersVisible, setIsAutresMetiersVisible] = useState(false);

  const categorieArticles = useMemo(
    () => articles.filter((article) => article.categorie === categorieDatabaseLabel),
    [articles, categorieDatabaseLabel]
  );

  const [selectedMetierCategorieArticles, autresMetiersCategorieArticles] = useMemo(() => {
    if (selectedMetierId === AllMetiersCode) {
      return [categorieArticles, []];
    } else {
      return [
        categorieArticles.filter(
          (article) => article.metiers?.map(({ id }) => id).includes(selectedMetierId) ?? false
        ),
        categorieArticles.filter(
          (article) => !(article.metiers?.map(({ id }) => id).includes(selectedMetierId) ?? false)
        ),
      ];
    }
  }, [categorieArticles, selectedMetierId]);

  return (
    <TabPanel
      value={categorieDatabaseLabel}
      key={categorieDatabaseLabel}
      sx={{ padding: { xs: 1, sm: 2 } }}>
      <Grid container size={12} sx={{ alignItems: "center" }} spacing={{ xs: 2, lg: 4 }}>
        <Grid container size={12}>
          {selectedMetierCategorieArticles?.map((article) => (
            <Grid key={article.id} size={{ xs: 12, lg: 6 }}>
              <ArticleCard article={article} addToCartUI isContentLink />
            </Grid>
          ))}
          {selectedMetierCategorieArticles.length === 0 && (
            <Grid size={12} sx={{ marginY: 4 }}>
              <Typography sx={{ textAlign: "center" }} color="text.primary">
                Il n'y a aucun article dans la catégorie "
                {categories.find(({ dataBaseLabel }) => dataBaseLabel === categorieDatabaseLabel)
                  ?.printLabel ?? "actuelle"}
                " pour le métier sélectionné
              </Typography>
            </Grid>
          )}
        </Grid>

        {autresMetiersCategorieArticles?.length > 0 && (
          <Grid container size={12} columnSpacing={1} rowSpacing={0}>
            <Grid
              container
              size={12}
              sx={{
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Grid
                container
                onClick={() => {
                  setIsAutresMetiersVisible(!isAutresMetiersVisible);
                }}
                sx={{
                  cursor: "pointer",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 2,
                  marginBottom: isAutresMetiersVisible ? -1.5 : 4,
                  transition: "margin-bottom .25s ease",
                }}>
                <IconButton size="small" sx={{ mr: -1 }}>
                  <Tooltip title={isAutresMetiersVisible ? "Fermer" : "Ouvrir"}>
                    {isAutresMetiersVisible ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  </Tooltip>
                </IconButton>
                <Link>
                  {isAutresMetiersVisible ? "Cacher" : "Voir"} les articles pour les autres métiers
                </Link>
              </Grid>
            </Grid>

            <Collapse in={isAutresMetiersVisible}>
              <Grid container size={12} spacing={{ xs: 2, lg: 4 }}>
                {autresMetiersCategorieArticles.map((article) => (
                  <Grid key={article.id} size={{ xs: 12, lg: 6 }}>
                    <ArticleCard article={article} addToCartUI isContentLink />
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Grid>
        )}
      </Grid>
    </TabPanel>
  );
}

export default CategorieCataloguePanel;
